import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  useTheme,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { trackFeature } from '../utils/analytics';

const formatKey = (key) => {
  if (!key) return '';
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
};

const formatFieldValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map((item) => formatFieldValue(item)).join(', ');
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${formatKey(k)}: ${formatFieldValue(v)}`)
      .join(', ');
  }
  return value.toString();
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const buildRows = (fields, data) => {
  if (!Array.isArray(fields)) return [];
  return fields
    .map((field) => {
      const rawValue = field.getValue ? field.getValue(data) : data?.[field.key];
      const formatted = field.format ? field.format(rawValue, data) : formatFieldValue(rawValue);
      const value = field.render ? field.render(rawValue, data, formatted) : formatted;
      const hideIfEmpty = field.hideIfEmpty !== false;
      const isEmpty = field.isEmpty ? field.isEmpty(rawValue, data) : isEmptyValue(formatted);
      if (hideIfEmpty && isEmpty) return null;
      return {
        label: field.label || formatKey(field.key),
        value,
      };
    })
    .filter(Boolean);
};

const InfoModalV2 = ({ open, onClose, schema, data, title }) => {
  const theme = useTheme();
  const location = useLocation();

  useEffect(() => {
    if (open) {
      trackFeature('info_modal_open', {
        modalTitle: title || schema?.title,
        pagePath: location.pathname,
      });
    }
  }, [open, title, schema, location.pathname]);

  const modalTitle = title || schema?.title || 'Details';
  const SectionCard = ({ title: sectionTitle, icon, children }) => (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      {sectionTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {sectionTitle}
          </Typography>
        </Box>
      )}
      {children}
    </Box>
  );

  const FieldRow = ({ label, value }) => (
    <Grid container spacing={2} sx={{ py: 0.75 }}>
      <Grid item xs={5}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={7}>
        {React.isValidElement(value) ? (
          value
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  if (!data || typeof data !== 'object' || !schema) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            maxHeight: { xs: '90vh', sm: '85vh' },
          },
        }}
      >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
        }}
      >
        {modalTitle}
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default }}>
        <SectionCard title="Summary">
          <Typography variant="body2" sx={{ opacity: 0.8 }}>No details available.</Typography>
        </SectionCard>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
  }

  const summaryRows = buildRows(schema.summary?.fields || [], data);
  const sections = Array.isArray(schema.sections) ? schema.sections : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          maxHeight: { xs: '90vh', sm: '85vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
        }}
      >
        {modalTitle}
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default }}>
        {summaryRows.length > 0 && (
          <SectionCard title={schema.summary?.title || 'Summary'}>
            {summaryRows.map((row) => (
              <FieldRow key={row.label} label={row.label} value={row.value} />
            ))}
          </SectionCard>
        )}

        {sections.map((section, index) => {
          const rows = buildRows(section.fields || [], data);
          if (rows.length === 0) return null;
          const isNotes = (section.title || '').toLowerCase().includes('notes');
          return (
            <SectionCard key={section.title || index} title={section.title}>
              {isNotes ? (
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    p: 1.5,
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.95rem',
                    lineHeight: 1.4,
                    opacity: 0.9,
                  }}
                >
                  {rows[0]?.value || '-'}
                </Box>
              ) : (
                rows.map((row) => (
                  <FieldRow key={row.label} label={row.label} value={row.value} />
                ))
              )}
            </SectionCard>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoModalV2;
